import { useState, useEffect, useCallback } from "react";
import { Mic2, X } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const GoLiveModal = ({ show, onClose, currentUser, authToken }) => {
  const [djForm, setDjForm] = useState({ dj_username: 'source', dj_password: '', stream_port: '8000', mount_point: '' });
  const [djCredentials, setDjCredentials] = useState(null);
  const [saving, setSaving] = useState(false);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (authToken) h['Authorization'] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  useEffect(() => {
    if (!show) return;
    const fetchCreds = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/station/dj-credentials`, { headers: headers() });
        if (res.ok) {
          const data = await res.json();
          setDjCredentials(data);
          setDjForm({
            dj_username: data.dj_username || 'source',
            dj_password: data.dj_password || '',
            stream_port: data.stream_port || '8000',
            mount_point: data.mount_point || `/${currentUser?.azuracast_station_id || ''}`
          });
        }
      } catch {}
    };
    fetchCreds();
  }, [show, headers, currentUser?.azuracast_station_id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/dj-credentials`, { method: 'PUT', headers: headers(), body: JSON.stringify(djForm) });
      if (res.ok) alert('DJ credentials saved!');
      else { const err = await res.json(); alert(err.detail || 'Failed to save credentials'); }
    } catch { alert('Failed to save credentials'); }
    finally { setSaving(false); }
  };

  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl p-6 max-w-lg w-full border border-white/10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center"><Mic2 className="w-5 h-5 text-purple-400" /></div>
            <div><h2 className="text-xl font-bold text-white">Go Live</h2><p className="text-white/50 text-sm">Connect your streaming software</p></div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <div className="bg-black/40 rounded-xl p-4 mb-4 space-y-3">
          <h3 className="text-white font-semibold mb-3">Connection Details</h3>
          <div>
            <label className="text-white/50 text-xs block mb-1">Server</label>
            <div className="flex items-center gap-2">
              <input type="text" value={djCredentials?.stream_server || 'djsandmc.media'} readOnly className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
              <button onClick={() => navigator.clipboard.writeText(djCredentials?.stream_server || 'djsandmc.media')} className="bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg text-white/70 text-sm">Copy</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-white/50 text-xs block mb-1">Port</label><input type="text" value={djForm.stream_port} onChange={(e) => setDjForm({...djForm, stream_port: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
            <div><label className="text-white/50 text-xs block mb-1">Mount Point</label><input type="text" value={djForm.mount_point} onChange={(e) => setDjForm({...djForm, mount_point: e.target.value})} placeholder={`/${currentUser?.azuracast_station_id || 'station'}`} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-white/50 text-xs block mb-1">Username</label><input type="text" value={djForm.dj_username} onChange={(e) => setDjForm({...djForm, dj_username: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
            <div><label className="text-white/50 text-xs block mb-1">Password</label><input type="password" value={djForm.dj_password} onChange={(e) => setDjForm({...djForm, dj_password: e.target.value})} placeholder="Enter source password" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving} className="w-full bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white py-3 rounded-xl mb-4 transition">{saving ? 'Saving...' : 'Save Credentials'}</button>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
          <h4 className="text-purple-400 font-semibold mb-2">Recommended Software</h4>
          <ul className="text-white/70 text-sm space-y-1"><li>BUTT (Broadcast Using This Tool) - Simple & free</li><li>Mixxx - Full DJ software with streaming</li><li>OBS Studio - For video + audio streaming</li><li>VirtualDJ - Professional DJ software</li></ul>
          <p className="text-white/50 text-xs mt-3">Enter the server, port, mount point, and credentials in your streaming software to go live.</p>
        </div>
      </div>
    </div>
  );
};

export default GoLiveModal;
